import React from 'react';
import { FormSection } from '../../common/FormSection';
import { BehaviourSupportPlanData } from 'src/models/form-types';

interface LearningSectionProps {
  formData: BehaviourSupportPlanData;
  onTextChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onCheckboxChange: (name: string, checked: boolean, category: keyof BehaviourSupportPlanData) => void;
  className?: string;
}

// Define the plan options
const planOptions = [
  {
    id: 'support-plasp',
    name: 'hasPLASP',
    label: 'Has Personalised Learning & Support Plan (PLASP)',
  },
  {
    id: 'support-rmp',
    name: 'hasRMP',
    label: 'Has Risk Management Plan (RMP)',
  },
  {
    id: 'support-brp',
    name: 'hasBRP',
    label: 'Has Behaviour Response Plan (BRP)',
  },
];

// Define the support type options
const adhdTypeOptions = [
  { value: 'Inattentive type', label: 'Inattentive type' },
  { value: 'Hyperactive type', label: 'Hyperactive type' },
  { value: 'Combined type', label: 'Combined type' },
];

// Define the support level options
const autismLevelOptions = [
  { value: 'Level 1', label: 'Level 1' },
  { value: 'Level 2', label: 'Level 2' },
  { value: 'Level 3', label: 'Level 3' },
];

export const LearningSection: React.FC<LearningSectionProps> = ({ 
  formData, 
  onTextChange, 
  onCheckboxChange, 
  className
}) => {
  const isADHDSelected = formData.learningSupport.includes('Diagnosis of ADHD');
  
  const isAutismSelected = formData.learningSupport.includes('Diagnosis of Autism Spectrum Disorder');

  const isNoneSelected = formData.learningSupport.includes('None');

  // Handle None logic for learningSupport
  const handleNoneCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Uncheck all other options and only check 'None'
      onCheckboxChange('None', true, 'learningSupport');
      if (formData.learningSupport.length > 0) {
        // Create a copy of the array to avoid unsafe iteration
        const currentOptions = [...formData.learningSupport];
        currentOptions.forEach(option => {
          if (option !== 'None') {
            onCheckboxChange(option, false, 'learningSupport');
          }
        });
      }
    } else {
      onCheckboxChange('None', false, 'learningSupport');
    }
  };

  // Modified handleLearningCheckboxChange to uncheck 'None' if another is checked
  const handleLearningCheckboxChange = (name: string, checked: boolean) => {
    if (name !== 'None' && checked && isNoneSelected) {
      onCheckboxChange('None', false, 'learningSupport');
    }
    onCheckboxChange(name, checked, 'learningSupport');
  };

  const handlePlanCheckboxChange = (name: string, checked: boolean) => {
    onCheckboxChange(name, checked, name as keyof BehaviourSupportPlanData);
  };

  return (
    <FormSection title="Learning & Support Information *" className={className}>
      <div className="learning-support-container">
        {/* ADHD Diagnosis with Support Type dropdown */}
        <div className="diagnosis-row">
          <label 
            className={`checkbox-item ${isADHDSelected ? 'selected' : ''}`}
            htmlFor="support-adhd"
          >
            <input
              type="checkbox"
              id="support-adhd"
              name="Diagnosis of ADHD"
              checked={isADHDSelected}
              onChange={(e) => handleLearningCheckboxChange('Diagnosis of ADHD', e.target.checked)}
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">Diagnosis of ADHD</span>
          </label>
          
          {/* Support Type dropdown - positioned to the right of ADHD checkbox */}
          <div className="dropdown-container">
            <label className="dropdown-label" htmlFor="adhdType">Support type</label>
            <select
              id="adhdType"
              name="adhdType"
              value={formData.adhdType}
              onChange={onTextChange}
              className={`dropdown-select ${!isADHDSelected ? 'disabled' : ''}`}
              disabled={!isADHDSelected}
            >
              <option value="">Select support type</option>
              {adhdTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Autism Diagnosis with Level dropdown */}
        <div className="diagnosis-row">
          <label 
            className={`checkbox-item ${isAutismSelected ? 'selected' : ''}`}
            htmlFor="support-autism"
          >
            <input
              type="checkbox"
              id="support-autism"
              name="Diagnosis of Autism Spectrum Disorder"
              checked={isAutismSelected}
              onChange={(e) => handleLearningCheckboxChange('Diagnosis of Autism Spectrum Disorder', e.target.checked)}
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">Diagnosis of Autism Spectrum Disorder</span>
          </label>
          
          {/* Level dropdown - positioned to the right of Autism checkbox */}
          <div className="dropdown-container">
            <label className="dropdown-label" htmlFor="autismLevel">Level</label>
            <select
              id="autismLevel"
              name="autismLevel"
              value={formData.autismLevel}
              onChange={onTextChange}
              className={`dropdown-select ${!isAutismSelected ? 'disabled' : ''}`}
              disabled={!isAutismSelected}
            >
              <option value="">Select level</option>
              {autismLevelOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Plan checkboxes */}
        <div className="plan-checkboxes-container">
          {planOptions.map(option => {
            const isChecked = Boolean(formData[option.name as keyof BehaviourSupportPlanData]);
            return (
              <label 
                className={`checkbox-item ${isChecked ? 'selected' : ''}`} 
                key={option.id}
                htmlFor={option.id}
              >
                <input
                  type="checkbox"
                  id={option.id}
                  name={option.name}
                  checked={isChecked}
                  onChange={(e) => handlePlanCheckboxChange(option.name, e.target.checked)}
                />
                <div className="checkbox-custom"></div>
                <span className="checkbox-label">{option.label}</span>
              </label>
            );
          })}
          {/* None checkbox as last option */}
          <label className={`checkbox-item ${isNoneSelected ? 'selected' : ''}`} htmlFor="support-none">
            <input
              type="checkbox"
              id="support-none"
              name="None"
              checked={isNoneSelected}
              onChange={e => handleNoneCheckboxChange(e.target.checked)}
            />
            <div className="checkbox-custom"></div>
            <span className="checkbox-label">None</span>
          </label>
        </div>
        
        {/* Other input field */}
        <div className="other-input-container">
          <label className="other-input-label" htmlFor="otherLearningSupport">Other:</label>
          <input
            type="text"
            id="otherLearningSupport"
            name="otherLearningSupport"
            value={formData.otherLearningSupport}
            onChange={onTextChange}
            className="other-input"
          />
        </div>
      </div>
    </FormSection>
  );
};
